import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { P2 } from "../../components/Paragraph";
import Button from "../../components/Button";
import { ReactComponent as SuccessBadge } from "../../components/badges/successBadge.svg";
import { ReactComponent as StarBadge } from "../../components/badges/starBadge.svg";
import { ReactComponent as FailBadge } from "../../components/badges/failBadge.svg";
import styles from "./challengePage.module.css";

const bloomColors = [
  "#648C94", "#82AABE", "#C0B87F",
  "#F4BC4D", "#E08A5E", "#F95146",
];

const bloomKeys = [
  "bloom.remember", "bloom.understand", "bloom.apply",
  "bloom.analyze", "bloom.evaluate", "bloom.create",
];

// Same topic data as dashboard
const mockTopics = [
  {
    id: "c1", cycle: "I", name: "Realieji skaičiai",
    sections: [
      { id: "s1", name: "Skaičių aibės", bloom: [100, 100, 100, 75, 0, 0] },
      { id: "s2", name: "Kvadratinė šaknis", bloom: [100, 100, 60, 0, 0, 0] },
      { id: "s3", name: "Veiksmai su realiaisiais skaičiais", bloom: [100, 45, 0, 0, 0, 0] },
      { id: "s4", name: "Kubinė šaknis", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c2", cycle: "II", name: "Konstravimas",
    sections: [
      { id: "s5", name: "Vektoriai", bloom: [100, 100, 80, 0, 0, 0] },
      { id: "s6", name: "Vektorių sudėtis ir atimtis", bloom: [100, 35, 0, 0, 0, 0] },
      { id: "s7", name: "Vektoriaus daugyba iš skaičiaus", bloom: [20, 0, 0, 0, 0, 0] },
      { id: "s8", name: "Objektų braižymas", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s9", name: "Modeliavimas", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c3", cycle: "III", name: "Algebriniai reiškiniai",
    sections: [
      { id: "s10", name: "Raidiniai reiškiniai", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s11", name: "Greitosios daugybos formulės", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s12", name: "Daugianarių skaidymas daugikliais", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c4", cycle: "IV", name: "Plokščiosios figūros",
    sections: [
      { id: "s13", name: "Pitagoro teorema", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s14", name: "Trikampio ir trapecijos vidurio linija", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s15", name: "Lygiašonio trikampio savybės", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c5", cycle: "V", name: "Finansiniai skaičiavimai",
    sections: [
      { id: "s16", name: "Procentinis didinimas arba mažinimas", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s17", name: "Paprastosios ir sudėtinės palūkanos", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c6", cycle: "VI", name: "Tiesiniai ir netiesiniai sąryšiai",
    sections: [
      { id: "s18", name: "Tiesinio sąryšio apibrėžtis", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s19", name: "Grafinis tiesinio sąryšio vaizdavimas", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c7", cycle: "VII", name: "Lygčių sistemos",
    sections: [
      { id: "s20", name: "Lygtis su dviem nežinomaisiais", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s21", name: "Sprendimas grafiniu būdu", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s22", name: "Sprendimas keitimo būdu", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s23", name: "Sprendimas sudėties būdu", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c8", cycle: "VIII", name: "Erdvinės figūros",
    sections: [
      { id: "s24", name: "Prizmės ir piramidės", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s25", name: "Ritinys ir kūgis", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s26", name: "Sfera ir rutulys", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
  {
    id: "c9", cycle: "IX", name: "Duomenys ir jų interpretavimas",
    sections: [
      { id: "s27", name: "Duomenų skirstymas intervalais", bloom: [0, 0, 0, 0, 0, 0] },
      { id: "s28", name: "Histograma ir kvartiliai", bloom: [0, 0, 0, 0, 0, 0] },
    ],
  },
];

const allSections = mockTopics.flatMap((c) =>
  c.sections.map((s) => ({ ...s, cycleName: c.name, cycle: c.cycle }))
);

const mockHistory = [
  { id: "h1", topic: "Suprastink tiesinę išraišką...", result: "success" },
  { id: "h2", topic: "Suskliausčiuok 8x + 4x + 1?", result: "assisted" },
  { id: "h3", topic: "Suskliausčiuok 5² - 3a² - 1", result: "success" },
  { id: "h4", topic: "Suskliausčiuok 8·1·k(x - 8)?", result: "gaveup" },
];

const FINISHED_COUNT = 8;
const READY_COUNT = 16;
const TOTAL = FINISHED_COUNT + READY_COUNT;

const ResultIcon = ({ result }) => {
  if (result === "success") return <SuccessBadge />;
  if (result === "assisted") return <StarBadge />;
  return <FailBadge />;
};

const DonutChart = ({ finished, ready, total }) => {
  const radius = 45;
  const strokeWidth = 20;
  const circumference = 2 * Math.PI * radius;
  let offset = circumference * 0.75;
  const slices = [
    { value: finished, color: "#82AABE" },
    { value: ready, color: "#F4C150" },
  ];
  return (
    <div style={{ width: "120px", height: "120px", position: "relative" }}>
      <svg width="120" height="120">
        <circle r={radius} cx="60" cy="60" fill="transparent" stroke="#eee" strokeWidth={strokeWidth} />
        {slices.map((slice, i) => {
          const dash = (slice.value / total) * circumference;
          const el = (
            <circle key={i} r={radius} cx="60" cy="60" fill="transparent"
              stroke={slice.color} strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset} strokeLinecap="butt" />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <h3 style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", margin: 0 }}>
        {total}
      </h3>
    </div>
  );
};

const Sidebar = ({ t }) => {
  const finishedPct = Math.round((FINISHED_COUNT / TOTAL) * 100);
  const unfinishedPct = 100 - finishedPct;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarInner}>
        <Link to="/mockups" style={{ display: "flex" }}>
          <img src="/logotype.png" alt={t("alt.logo")} className={styles.logo} />
        </Link>

        <div className={styles.classLink}>
          <h4 style={{ margin: 0, fontWeight: 800 }}>8 klasė</h4>
          <div className={styles.menuSpacer} />
        </div>

        <div className={styles.menuLink}>{t("nav.tasks")}</div>
        <div className={styles.menuLink}>{t("nav.lessons")}</div>
        <div className={styles.menuLink}>{t("nav.mastery")}</div>

        <div style={{ flexGrow: 1 }} />

        <div className={styles.widget}>
          <h4 className={styles.widgetHeader}>{t("nav.history")}</h4>
          {mockHistory.map((item) => (
            <div key={item.id} className={styles.historyLine}>
              <ResultIcon result={item.result} />
              <P2 style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.topic}</P2>
            </div>
          ))}
        </div>

        <div className={styles.widgetStats}>
          <h4 className={styles.widgetHeader}>{t("nav.statistics")}</h4>
          <P2 style={{ margin: 0 }}>{t("statistics.problemsTackled")}</P2>
          <div className={styles.donutContainer}>
            <DonutChart finished={FINISHED_COUNT} ready={READY_COUNT} total={TOTAL} />
          </div>
          <div className={styles.legendRow}>
            <b style={{ color: "#385654" }}>{finishedPct}%</b>
            <span>{t("statistics.finished")}</span>
          </div>
          <div className={styles.legendRow}>
            <b style={{ color: "#865c08" }}>{unfinishedPct}%</b>
            <span>{t("statistics.unfinished")}</span>
          </div>
        </div>
      </div>
    </aside>
  );
};

const ChallengePage = () => {
  const { t } = useTranslation();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [expandedCycles, setExpandedCycles] = useState({});

  const toggleCycle = (id) =>
    setExpandedCycles((prev) => ({ ...prev, [id]: !prev[id] }));

  const section = selectedTopic
    ? allSections.find((s) => s.id === selectedTopic)
    : null;

  return (
    <div className={styles.layout}>
      <Sidebar t={t} />
      <main className={styles.main}>
        <div className={styles.content}>
          <h2 className={styles.title}>{t("challenge.title")}</h2>

          {/* Step 1: Topic selection */}
          <div className={styles.card}>
            <h3 className={styles.cardSubtitle}>{t("challenge.selectTopic")}</h3>
            <div className={styles.topicTree}>
              {mockTopics.map((cycle) => {
                const isExpanded = expandedCycles[cycle.id] !== false;
                return (
                  <div key={cycle.id}>
                    <div className={styles.cycleHeader} onClick={() => toggleCycle(cycle.id)}>
                      <span className={styles.cycleToggle}>{isExpanded ? "▾" : "▸"}</span>
                      <span className={styles.cycleName}>{cycle.cycle}. {cycle.name}</span>
                    </div>
                    {isExpanded && cycle.sections.map((sec) => {
                      const started = sec.bloom.some((v) => v > 0);
                      const isSelected = selectedTopic === sec.id;
                      return (
                        <div
                          key={sec.id}
                          className={`${styles.topicItem} ${isSelected ? styles.topicItemSelected : ""}`}
                          onClick={() => { setSelectedTopic(sec.id); setSelectedLevel(null); }}
                        >
                          <P2 style={{ margin: 0 }}>{sec.name}</P2>
                          {started && (
                            <div className={styles.topicMiniBloom}>
                              {sec.bloom.map((pct, i) => (
                                <div key={i} className={styles.miniDot}
                                  style={{ background: pct > 0 ? bloomColors[i] : "#e0e0e0", opacity: pct > 0 ? Math.max(0.4, pct / 100) : 1 }} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 2: Level selection (only when topic is selected) */}
          {section && (
            <div className={styles.card}>
              <div className={styles.selectedHeader}>
                <P2 style={{ margin: 0, color: "#999" }}>{section.cycle}. {section.cycleName}</P2>
                <h3 className={styles.selectedName}>{section.name}</h3>
              </div>

              <h4 className={styles.cardSubtitle}>{t("challenge.selectLevel")}</h4>
              <div className={styles.levelGrid}>
                {bloomKeys.map((key, i) => {
                  const pct = section.bloom[i];
                  const isSelected = selectedLevel === i;
                  return (
                    <div
                      key={key}
                      className={`${styles.levelCard} ${isSelected ? styles.levelCardSelected : ""}`}
                      style={{ borderColor: isSelected ? bloomColors[i] : undefined }}
                      onClick={() => setSelectedLevel(i)}
                    >
                      <div className={styles.levelColorBar} style={{ background: bloomColors[i] }} />
                      <div className={styles.levelContent}>
                        <span className={styles.levelName}>{t(key)}</span>
                        <span className={styles.levelPct}>{pct > 0 ? `${pct}%` : t("challenge.notStarted")}</span>
                        <div className={styles.levelBarBg}>
                          <div className={styles.levelBarFill} style={{ width: `${pct}%`, background: bloomColors[i] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.startRow}>
                <Button
                  important large
                  disabled={selectedLevel === null}
                  onClick={() => alert(`${section.name} — ${t(bloomKeys[selectedLevel])}`)}
                >
                  {t("challenge.start")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChallengePage;
